import { atom } from "jotai";

import { type AppConfig, appConfigSchema } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { env } from "../env/env";
import { exists, mkdir, readJsonFile, writeJsonFile } from "../filesystem/filesystem.internal";
import { getConfigFilePath, getMomentDirPath } from "../paths/paths.internal";

const logger = new Logger("anonymous-id");

/**
 * Loads or creates a persistent anonymous user ID from `moment.json`.
 * This ID survives app restarts and is used for analytics when the user
 * is not logged in.
 */
export const $anonymousUserId = atom(async (): Promise<string> => {
	if (!env.isDesktopApp && !env.isMobileApp) {
		return appConfigSchema.parse({}).userID;
	}

	try {
		const configPath = await getConfigFilePath();
		const configExists = await exists(configPath);

		if (!configExists) {
			const config = appConfigSchema.parse({});
			const momentDir = await getMomentDirPath();
			await mkdir(momentDir);
			await writeJsonFile(configPath, config);
			return config.userID;
		}

		const config = await readJsonFile<AppConfig>(configPath);
		if (config?.userID) {
			return config.userID;
		}

		// Config exists but has no userID — add one
		const updated = appConfigSchema.parse(config ?? {});
		await writeJsonFile(configPath, updated);
		return updated.userID;
	} catch (error) {
		logger.error("Failed to load anonymous user ID", { error });
		return appConfigSchema.parse({}).userID;
	}
});
$anonymousUserId.debugLabel = "anonymousUserId";
